import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _set from "lodash/set";
import _get from "lodash/get";
import _difference from "lodash/difference";
var _dec, _class, _descriptor, _descriptor2;
/**
 * Collection to manage asset tag assignment of models selected from an asset-specific view (Devices list view etc).
 */
import { action, computed, observable } from 'mobx';
import api from 'core/util/api';
import { getLabelTypeValue, TARGET_ACTION } from '@kentik/ui-shared/labels/lib';
import $assetTags from 'app/stores/assetTags/$assetTags';
import { Collection } from 'core/model/Collection';
import AssetTagInventoryModel from './AssetTagInventoryModel';
let AssetTagInventoryCollection = (_dec = observable.shallow, _class = class AssetTagInventoryCollection extends Collection {
  constructor(data = [], {
    assetType,
    assetIds,
    singleMode
  }) {
    super(data);
    /**
     * A cache to store group-specific data, such as selected values and options for each tag group.
     * This helps manage selected state for target values and assets as well as caching selectable options to prevent unnecessary fetches.
     */
    _initializerDefineProperty(this, "groupCache", _descriptor, this);
    /**
     * A set of asset tag values marked for permanent removal
     */
    _initializerDefineProperty(this, "removedIds", _descriptor2, this);
    /**
     * Indicates the type of asset we're managing assets tags for
     */
    this.assetType = void 0;
    /**
     * A unique list of all the available assets we can tag
     */
    this.assetIds = [];
    /**
     * Singlemode occurs when we're managing tags for a single inventory item via that asset type's edit view
     */
    this.singleMode = false;
    this.assetType = assetType;
    this.assetIds = assetIds;
    this.singleMode = singleMode;
  }
  get model() {
    return AssetTagInventoryModel;
  }
  get url() {
    return `/api/ui/assetTags/inventory/${this.assetType}`;
  }
  get fetchMethod() {
    return 'post';
  }
  get defaultGroupBy() {
    return 'tag_id';
  }
  get defaultSortState() {
    return {
      field: 'tag_name',
      direction: 'asc'
    };
  }

  /**
   * Provides generic labeling for assets in the inventory.
   * When we're managing devices, this will return "Device" etc.
   */
  get assetLabel() {
    return getLabelTypeValue(this.assetType) || 'Asset';
  }

  /**
   * Provides the total count of assets targeted in the inventory
   */
  get totalAssetCount() {
    return this.assetIds.length;
  }

  /**
   * Groups models by tag id that provides:
   *  - modelIds: asset tag value ids
   *  - assetIds: asset ids (device ids etc)
   *  - tagRef: single, shared reference data representing a tag id/name. This is used when filling in new models when we target 'all' assets
   */
  get tags() {
    const tagHash = {};
    this.get().forEach(model => {
      const {
        tag_id,
        tag_name,
        asset_id,
        description
      } = model.get();
      if (!tagHash[tag_id]) {
        tagHash[tag_id] = {
          modelIds: [],
          assetIds: [],
          tagRef: {
            tag_id,
            tag_name,
            description
          }
        };
      }
      tagHash[tag_id].modelIds.push(model.id);
      tagHash[tag_id].assetIds.push(asset_id);
    });
    return tagHash;
  }
  get errorCount() {
    return this.get().filter(model => model.hasError).length;
  }
  get addedCount() {
    return this.get().filter(model => model.isNewValue).length;
  }
  get editedCount() {
    return this.get().filter(model => model.valueEdited).length;
  }
  get unchangedCount() {
    return this.get().filter(model => model.valueUnchanged).length;
  }
  get removedCount() {
    return this.removedIds.size;
  }
  get canSave() {
    return this.errorCount === 0 && (this.addedCount > 0 || this.editedCount > 0 || this.removedCount > 0);
  }

  /**
   * Fetches available tag options for the asset tags inventory.
   */
  async fetchTagOptions() {
    return api.get('/api/ui/assetTags/keys').then(tags => tags.map(({
      id,
      name,
      display_name,
      description
    }) => ({
      value: id,
      label: display_name || name,
      description
    }))).catch(err => {
      console.error('Error fetching tags', err);
      return [];
    });
  }

  /**
   * Fetches available tag values for a specific tag, caching on first fetch.
   * In single mode, we remove the option to 'keep as is' and will instead show the matched value
   */
  async fetchTagValues({
    tagId
  }) {
    const cachedOptions = this.getGroupCache({
      tagId,
      key: 'options'
    });
    if (cachedOptions) {
      return cachedOptions;
    }

    // initial option is always to 'keep as is'
    const options = this.singleMode ? [] : [{
      value: TARGET_ACTION.KEEP.value,
      label: TARGET_ACTION.KEEP.label
    }];
    return api.get(`/api/ui/assetTags/keys/${tagId}/values?unique=true`).then(tagValues => {
      const tagValueOptions = options.concat(tagValues.map(tagValue => ({
        label: tagValue.value,
        value: tagValue.value
      })).sort((a, b) => a.value.localeCompare(b.value)));

      // cache the options
      this.setGroupCache({
        tagId,
        key: 'options',
        value: tagValueOptions
      });
      return tagValueOptions;
    }).catch(err => {
      console.error(`Error fetching tag values for tagId: ${tagId}`, err);
      return options;
    });
  }

  /**
   * Cache helper for things like value/device selections and selectable options that require fetch.
   */
  setGroupCache({
    tagId,
    key,
    value
  }) {
    _set(this.groupCache, `${tagId}.${key}`, value);
  }
  getGroupCache({
    tagId,
    key
  }) {
    return _get(this.groupCache, `${tagId}.${key}`);
  }

  /**
   * Determine whether a tag group contains all new values.
   * This is a key logic piece when resetting groups and determining auto-disabled states for target devices.
   */
  tagGroupIsAllNew({
    tagId
  }) {
    const tag = this.tags[tagId];
    if (tag) {
      return tag.modelIds.every(modelId => this.modelById[modelId]?.isNewValue);
    }
    return false;
  }

  /**
   * Target device selection (keep as is / all) is disabled when all assets are tagged and they are either all pristine or all assigned.
   * In these situations, the choice between keeping and assigning all is the same so we lock in an 'all' selection.
   */
  tagAssetIsDisabled({
    tagId
  }) {
    const tag = this.tags[tagId];
    if (tag) {
      return tag.modelIds.length === this.assetIds.length && (tag.modelIds.every(modelId => !this.modelById[modelId]?.isNewValue) || this.tagGroupIsAllNew({
        tagId
      }));
    }
    return false;
  }

  /**
   * Determines whether a tag group can be reset to its original state.
   * We can reset so long as the group isn't completely new and at least one asset has a new or edited value.
   */
  canReset({
    tagId
  }) {
    const tag = this.tags[tagId];
    if (tag) {
      return !this.tagGroupIsAllNew({
        tagId
      }) && tag.modelIds.some(modelId => this.modelById[modelId]?.isNewValue || this.modelById[modelId]?.valueEdited);
    }
    return false;
  }

  /**
   * Returns the initial target value, first checking cache.
   * Ensures current selections are reflecting as the table row scrolls in/out of view.
   * When operating in single mode, the concept of 'keep as is' is removed. Instead we'll show the matched value
   * or in the case of a new tag, a prompt to make a selection.
   */
  initTargetValue({
    tagId
  }) {
    if (this.singleMode) {
      const targetModel = this.get().find(model => model.get('tag_id') === tagId);
      if (targetModel) {
        return targetModel.get('target_value') || 'Select a value';
      }
    }
    return this.getGroupCache({
      tagId,
      key: 'value'
    }) || TARGET_ACTION.KEEP.value;
  }

  /**
   * Returns the initial target asset selection, first checking cache.
   * Ensures current selections are reflecting as the table row scrolls in/out of view.
   * Will lock down an 'all' selection when seen as disabled, otherwise falling back to 'keep'.
   */
  initTargetAsset({
    tagId
  }) {
    if (this.tagAssetIsDisabled({
      tagId
    })) {
      return TARGET_ACTION.ALL.value;
    }
    return this.getGroupCache({
      tagId,
      key: 'asset'
    }) || TARGET_ACTION.KEEP.value;
  }

  /**
   * Helper that determines the default target value when adding new items.
   * If the tag group has existing items all with the same value, new items will also take on that value.
   * Otherwise, they get blank values that result in an error state asking for a selection.
   */
  getDefaultTargetValue(tagId) {
    const tag = this.tags[tagId];
    if (tag) {
      const targetValues = Array.from(new Set(tag.modelIds.map(modelId => this.modelById[modelId]?.get('target_value') || '')));
      if (targetValues.length === 1) {
        return targetValues[0];
      }
    }
    return '';
  }

  /**
   * Indicates the number of errors in a tag group (those that require a selection).
   * Used as a display badge in the value selector.
   */
  getTagErrorCount(tagId) {
    const tag = this.tags[tagId];
    if (tag) {
      return tag.modelIds.filter(modelId => this.modelById[modelId]?.hasError).length;
    }
    return 0;
  }

  /**
   *
   * After filling in new values for assets, we could have an explicit target value to show in the selector (all being the same)
   * If that happens, set the cache explicitly so the selector shows the new value for the group instead of 'keep as is'
   */
  updateDefaultTargetValue(tagId) {
    const defaultTargetValue = this.getDefaultTargetValue(tagId);
    if (defaultTargetValue) {
      this.setGroupCache({
        tagId,
        key: 'value',
        value: defaultTargetValue
      });
    }
  }

  /**
   * Adds a new tag to the collection.
   * Blank tag values are added for each available asset in the inventory.
   */
  addTag({
    tagId,
    tagName,
    description
  }) {
    const assetIdsToAdd = this.assetIds.map(assetId => ({
      tag_id: tagId,
      tag_name: tagName,
      asset_id: assetId,
      asset_type: this.assetType,
      asset_name: $assetTags.getAssetName({
        assetType: this.assetType,
        assetId
      }),
      value: '',
      target_value: '',
      description
    }));
    this.add(assetIdsToAdd);
    this.updateDefaultTargetValue(tagId);
  }

  /**
   * Fills in asset tag values for any assets not already tagged in the group.
   * This is the result of selecting 'All' as the target device.
   */
  targetAllAssets(tagId) {
    const tag = this.tags[tagId];
    if (tag) {
      const target_value = this.getDefaultTargetValue(tagId);
      const assetIdsToAdd = _difference(this.assetIds, tag.assetIds).map(assetId => ({
        ...tag.tagRef,
        asset_id: assetId,
        asset_type: this.assetType,
        asset_name: $assetTags.getAssetName({
          assetType: this.assetType,
          assetId
        }),
        value: '',
        target_value
      }));
      this.add(assetIdsToAdd);
    }
  }

  /**
   * When we choose 'Keep as is' for the target device, new values if any, are removed from the group.
   * This basically reinstates the original state of the group before any new values were added.
   */
  keepAssetsAsIs(tagId) {
    const tag = this.tags[tagId];
    if (tag) {
      const modelsToRemove = tag.modelIds.filter(modelId => this.modelById[modelId]?.isNewValue);
      if (modelsToRemove.length) {
        this.remove(modelsToRemove);
      }
    }
  }

  /**
   * Updates the target value for all assets in a tag group using the target value selector.
   * If we 'keep as is', values are reset to their original value.
   */
  updateGroupValues({
    tagId,
    value
  }) {
    const tag = this.tags[tagId];
    if (tag) {
      // update the group cache to save the group's selected value
      this.setGroupCache({
        tagId,
        key: 'value',
        value
      });
      tag.modelIds.forEach(modelId => {
        const model = this.modelById[modelId];
        if (model) {
          if (value === TARGET_ACTION.KEEP.value) {
            model.set('target_value', model.get('value'));
          } else {
            model.set('target_value', value);
          }
        }
      });
    }
  }

  /**
   * Updates the target asset selection for all assets in a tag group using the target asset selector.
   * 'keep as is' will remove new values, 'all' will fill in new values for available assets.
   */
  updateGroupAssets({
    tagId,
    value
  }) {
    // update the group cache to save the group's selected value
    this.setGroupCache({
      tagId,
      key: 'asset',
      value
    });
    if (value === TARGET_ACTION.KEEP.value) {
      this.keepAssetsAsIs(tagId);
    } else if (value === TARGET_ACTION.ALL.value) {
      this.targetAllAssets(tagId);
      this.updateDefaultTargetValue(tagId);
    }
  }

  /**
   * Allows resetting a tag group to its original state.
   * Groups that are completely new won't get this option because there is only one option (pick a value).
   */
  resetGroup(tagId) {
    this.updateGroupValues({
      tagId,
      value: TARGET_ACTION.KEEP.value
    });
    if (!this.tagGroupIsAllNew({
      tagId
    })) {
      this.updateGroupAssets({
        tagId,
        value: TARGET_ACTION.KEEP.value
      });
    }
  }

  /**
   * Existing tag values are marked for permanent removal via API.
   * New values are simply removed from the collection.
   */
  removeValues(models) {
    models.forEach(model => {
      if (!model.isNew) {
        // mark for persistent removal via API
        this.removedIds.add(model.id);
      }

      // remove from view
      this.remove(model.id);
    });
    const tagId = models[0]?.get('tag_id');
    if (tagId) {
      // clear the value cache so if it's added back it'll get a default selection state
      this.setGroupCache({
        tagId,
        key: 'value',
        value: undefined
      });
    }
  }

  // @ts-expect-error intentional override
  serialize({
    asset_id: asset_id_override
  } = {}) {
    const updatesByTagId = {};
    this.get().filter(model => model.canSave).forEach(model => {
      const {
        asset_id,
        asset_type,
        tag_id,
        target_value
      } = model.get();
      if (!updatesByTagId[tag_id]) {
        updatesByTagId[tag_id] = {
          tag_id,
          value: target_value,
          asset_type,
          asset_ids: []
        };
      }
      updatesByTagId[tag_id].asset_ids.push(asset_id_override || asset_id);
    });
    return {
      values: {
        del: Array.from(this.removedIds),
        set: Object.values(updatesByTagId)
      }
    };
  }

  /**
   * Executes a bulk update of all asset tag values, only sending those that have updates to commit.
   */
  async updateAssetTagValues({
    asset_id
  } = {}) {
    this.setRequestStatus('fetching');
    return api.put('/api/ui/assetTags/values', {
      data: this.serialize({
        asset_id
      })
    }).finally(() => {
      this.setRequestStatus(null);
    });
  }
  deserialize(data) {
    return data.map(item => ({
      ...item,
      target_value: item.value,
      asset_name: $assetTags.getAssetName({
        assetType: item.asset_type,
        assetId: item.asset_id
      }) || ''
    }));
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "groupCache", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "removedIds", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Set();
  }
}), _applyDecoratedDescriptor(_class.prototype, "assetLabel", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "assetLabel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "tags", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "tags"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "errorCount", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "errorCount"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addedCount", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "addedCount"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "editedCount", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "editedCount"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unchangedCount", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "unchangedCount"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removedCount", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "removedCount"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canSave", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canSave"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setGroupCache", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setGroupCache"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateDefaultTargetValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateDefaultTargetValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addTag", [action], Object.getOwnPropertyDescriptor(_class.prototype, "addTag"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "targetAllAssets", [action], Object.getOwnPropertyDescriptor(_class.prototype, "targetAllAssets"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "keepAssetsAsIs", [action], Object.getOwnPropertyDescriptor(_class.prototype, "keepAssetsAsIs"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateGroupValues", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateGroupValues"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateGroupAssets", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateGroupAssets"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetGroup", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetGroup"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeValues", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeValues"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateAssetTagValues", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateAssetTagValues"), _class.prototype), _class);
export default AssetTagInventoryCollection;